import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Slider,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import { get, set } from "lodash";
import { FC, useEffect, useState } from "react";
import { createDefaultVital } from ".";
import SelectButton from "../../../../../../components/shortcuts/components/SelectButton";
import { VitalsInputProps } from "../../../../../../components/widgets/modals/encounter";
import CodingSystems from "../../../../../../constants/CodingSystems";
import usePractitioner from "../../../../../../hooks/usePractitioner";
import { testRegExp } from "../../../../../../utils/regex";
import {
  createObservationComponent,
  updateMetaData,
} from "../VitalsFormHelper";
import { useMedicalConfig } from "../../../../../../slices/configuration";
import { timeSpans } from "../../../../../../slices/options/general";
import { createCodeableConcept } from "../../../../../../utils/DataUtils";
import { Vital } from "../../../../../../types/Vitals";
import { CodeableConcept } from "../../../../../../types/codeableConcept";
import { duration } from "moment";
import CdsFeedbackTextField from "./CdsFeedbackTextField";
import CdsArrowTooltip from "./cdsArrowTooltip";
import { calculatePainScaleFeedback } from "../../../../../../utils/painScaleUtils";
import { getBorderColor } from "./CdsFeedbackTextField";
import { useTheme } from "@mui/material/styles";

interface PainScaleProps extends VitalsInputProps {
  showCheckbox: boolean;
  capturePain: boolean;
  onChangeExperiencingPain: (e: boolean) => void;
}

const sliderColorMap = {
  0: "#4DB53D",
  1: "#71BB30",
  2: "#95C223",
  3: "#B9C816",
  4: "#DCCF09",
  5: "#EFC104",
  6: "#F09D08",
  7: "#F17A0B",
  8: "#F2560E",
  9: "#F33112",
  10: "#F32114",
};

export const getPainScaleEmoji = (value: number) => {
  switch (true) {
    case value === 0: {
      return "😃";
    }
    case value <= 2: {
      return "🙂";
    }
    case value <= 4: {
      return "😕";
    }
    case value <= 6: {
      return "🙁";
    }
    case value <= 8: {
      return "😢";
    }
    case value <= 10: {
      return "🤕";
    }
    default:
      return "";
  }
};

type Component = {
  valueQuantity: {
    value: number;
    unit: string;
    code: string;
  };
  code: CodeableConcept;
};

const generatePainString = (painLevel: Component, painDiration: Component) => {
  const level = get(painLevel, "valueQuantity.value");
  if (!painLevel) {
    return undefined;
  }
  const duration = get(painDiration, "valueQuantity.value");
  const unit = get(painDiration, "valueQuantity.code");
  const pain = `Level: ${level} ${duration ? `- Duration: ${duration} ${unit ? unit : ""}` : ""}`;
  return pain;
};

const PainInput: FC<PainScaleProps> = ({
  encounter,
  patient,
  disabled,
  value,
  onChange,
  size,
  showCheckbox,
  capturePain,
  onChangeExperiencingPain,
}) => {
  const practitionerReference = usePractitioner();
  const theme = useTheme();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [painSeverity, setPainSeverity] = useState<any>(
    createObservationComponent(
      createCodeableConcept(
        CodingSystems.OBSERVATION_CODE,
        "38208-5",
        "Pain Severity",
      ),
      0,
      "",
      "",
    ),
  );
  const [painDuration, setPainDuration] = useState<any>(
    createObservationComponent(
      createCodeableConcept(
        CodingSystems.OBSERVATION_CODE,
        "38207-7",
        "Pain Duration",
      ),
      undefined,
      "h",
      "h",
    ),
  );
  const [painLevel, setPainLevel] = useState<any>();
  const [pain, setPain] = useState<Vital>(
    createDefaultVital(
      encounter,
      patient,
      CodingSystems.OBSERVATION_CODE,
      "38212-7",
      "Pain",
      "",
    ),
  );

  useEffect(() => {
    const level = get(value, "component[0].valueQuantity.value");

    const numericLevel =
      level !== undefined && level !== null ? Number(level) : 0;
    setPainLevel(numericLevel);
  }, [value]);

  useEffect(() => {
    if (!capturePain) {
      const defaultSeverity = createObservationComponent(
        createCodeableConcept(
          CodingSystems.OBSERVATION_CODE,
          "38208-5",
          "Pain Severity",
        ),
        undefined,
        "",
        "",
      );
      const defaultDuration = createObservationComponent(
        createCodeableConcept(
          CodingSystems.OBSERVATION_CODE,
          "38207-7",
          "Pain Duration",
        ),
        undefined,
        "h",
        "h",
      );
      const newVital = { ...pain };
      set(newVital, "component[0]", defaultSeverity);
      set(newVital, "component[1]", defaultDuration);
      set(newVital, "valueQuantity.value", undefined);
    }
  }, [capturePain]);

  useEffect(() => {
    if (value && value.component) {
      setPain({ ...pain, ...value });
      if (value.component[0]) {
        setPainSeverity(get(value, "component[0]"));
      }
      if (value.component[1]) {
        setPainDuration(get(value, "component[1]"));
      }
      return;
    }
  }, [value]);

  // Calculate feedback based on pain level
  const feedback = calculatePainScaleFeedback(painLevel || 0);

  return (
    <Grid item xs={12}>
      <Grid container>
        {showCheckbox && (
          <Grid item xs={12}>
            <FormControlLabel
              label={`Experiencing pain? ${getPainScaleEmoji(painLevel)}`}
              control={
                <Checkbox
                  checked={!disabled && capturePain}
                  onChange={(e) => {
                    onChangeExperiencingPain(!capturePain);
                  }}
                />
              }
            />
          </Grid>
        )}
        <Grid container spacing={3}>
          <Grid item xs={8}>
            <FormControl fullWidth>
              <CdsArrowTooltip
                type={feedback?.type}
                feedbackText={feedback?.message}
                openCDS={typeof painLevel === "number" && painLevel >= 0}
              >
                <Box sx={{ mt: 1, px: 1 }}>
                  <Slider
                    marks={[
                      { value: 0, label: "0" },
                      { value: 1, label: "1" },
                      { value: 2, label: "2" },
                      { value: 3, label: "3" },
                      { value: 4, label: "4" },
                      { value: 5, label: "5" },
                      { value: 6, label: "6" },
                      { value: 7, label: "7" },
                      { value: 8, label: "8" },
                      { value: 9, label: "9" },
                      { value: 10, label: "10" },
                    ]}
                    sx={{
                      color: getBorderColor(
                        feedback?.type,
                        theme.palette,
                        theme.palette.mode,
                      ),
                      "& .MuiSlider-thumb": {
                        backgroundColor: getBorderColor(
                          feedback?.type,
                          theme.palette,
                          theme.palette.mode,
                        ),
                      },
                      "& .MuiSlider-track": {
                        backgroundColor: getBorderColor(
                          feedback?.type,
                          theme.palette,
                          theme.palette.mode,
                        ),
                      },
                      "& .MuiSlider-rail": {
                        opacity: 0.3,
                      },
                    }}
                    disabled={disabled}
                    value={typeof painLevel === "number" ? painLevel : 0}
                    max={10}
                    min={0}
                    onChange={(e, val) => {
                      const severity = { ...painSeverity };
                      set(severity, "valueQuantity.value", val);
                      const newVital = { ...pain, ...value };
                      set(newVital, "component[0]", severity);
                      const painString = generatePainString(
                        severity,
                        painDuration,
                      );
                      set(newVital, "valueQuantity.value", painString);
                      onChange({
                        updated: true,
                        ...newVital,
                      });
                    }}
                  />
                </Box>
              </CdsArrowTooltip>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <Typography>&nbsp;</Typography>
            <FormControl fullWidth>
              <TextField
                id="vitals-pain-duration"
                key={"vital-pain-duration-input"}
                disabled={disabled}
                value={
                  painDuration?.valueQuantity?.value
                    ? painDuration?.valueQuantity?.value
                    : ""
                }
                onChange={(e) => {
                  const targetValue = parseInt(e.target.value);
                  const duration = {
                    ...painDuration,
                    valueQuantity: {
                      ...painDuration?.valueQuantity,
                      value: targetValue,
                    },
                  };
                  const newVital = { ...pain, ...value };
                  set(newVital, "component[1]", duration);
                  const painString = generatePainString(painSeverity, duration);
                  set(newVital, "valueQuantity.value", painString);
                  onChange({
                    updated: true,
                    ...newVital,
                  });
                }}
                label="Duration"
                inputMode="numeric"
                autoComplete="off"
                size={"small"}
                InputProps={{
                  endAdornment: (
                    <SelectButton
                      label={get(painDuration, "valueQuantity.unit") || "h"}
                      options={timeSpans}
                      variant="text"
                      disabled={disabled}
                      onSelect={(unit) => {
                        const duration = {
                          ...painDuration,
                          valueQuantity: {
                            ...painDuration?.valueQuantity,
                            code: unit.code,
                            unit: unit.code,
                          },
                        };
                        const newVital = { ...pain, ...value };
                        set(newVital, "component[1]", duration);
                        const painString = generatePainString(
                          painSeverity,
                          duration,
                        );
                        set(newVital, "valueQuantity.value", painString);
                        onChange({
                          updated: true,
                          ...newVital,
                        });
                      }}
                      size={size || "small"}
                      getOptionLabel={(value) => value?.display}
                    />
                  ),
                }}
                variant="outlined"
              />
            </FormControl>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PainInput;
